import { defineStore } from 'pinia'

/**
 * Router store for tracking navigation state and history
 * Provides reliable back/forward button state management
 */
export const useRouterStore = defineStore('useRouterStore', () => {
  // State
  const isNavigating = ref(false)
  const historyStack = ref([])
  const currentIndex = ref(-1)
  const navigationTrackingInitialized = ref(false)
  let routerInstance = null

  // Computed
  const canGoBack = computed(() => {
    // Check our internal history stack first
    if (currentIndex.value > 0 || historyStack.value.length > 1) {
      return true
    }

    // Fallback: check browser history if available
    if (typeof window !== 'undefined' && window.history) {
      // If we have history length > 1, we can go back
      // Note: This is a fallback and may not be 100% accurate in all scenarios
      return window.history.length > 1
    }

    return false
  })

  const canGoForward = computed(() => {
    // Check our internal history stack first
    if (currentIndex.value < historyStack.value.length - 1) {
      return true
    }

    // For forward, we can't reliably detect from browser history alone
    // So we primarily rely on our internal tracking
    return false
  })

  // Actions
  /**
   * Initialize router tracking by setting up navigation hooks
   * @param {Object} router - Vue Router instance
   */
  function initializeRouterTracking(router) {
    if (navigationTrackingInitialized.value || !router) {
      return
    }

    routerInstance = router

    // Set up navigation hooks
    router.beforeEach((to, from) => {
      isNavigating.value = true

      return true
    })

    router.afterEach((to, from) => {
      isNavigating.value = false

      // Update history stack
      const currentPath = to.fullPath

      // If we're going back/forward, update index
      // Otherwise, add new entry to stack
      if (historyStack.value.length === 0) {
        // First navigation - add both from and to paths
        if (from.fullPath) {
          historyStack.value.push(from.fullPath)
        }
        historyStack.value.push(currentPath)
        currentIndex.value = historyStack.value.length - 1
      } else {
        // Check if this path already exists in our stack (back/forward navigation)
        const existingIndex = historyStack.value.findIndex(path => path === currentPath)

        if (existingIndex !== -1) {
          // This is a back/forward navigation to an existing path
          currentIndex.value = existingIndex
        } else {
          // New navigation - add to stack
          // If we're not at the end, truncate forward history
          if (currentIndex.value < historyStack.value.length - 1) {
            historyStack.value = historyStack.value.slice(0, currentIndex.value + 1)
          }
          historyStack.value.push(currentPath)
          currentIndex.value = historyStack.value.length - 1
        }
      }
    })

    navigationTrackingInitialized.value = true
  }

  /**
   * Get the router instance (if available)
   * @returns {Object|null} Router instance or null
   */
  function getRouter() {
    return routerInstance
  }

  /**
   * Lazy initialization - attempts to get router from dynamic import
   * This is useful when the caller already has a router instance available
   * but wants to avoid wiring the guard setup twice.
   */
  function initializeNavigationTracking(router) {
    if (navigationTrackingInitialized.value || !router) {
      return
    }

    if (typeof router.beforeEach === 'function') {
      initializeRouterTracking(router)
    }
  }

  /**
   * Reset navigation state (useful for testing or reset scenarios)
   */
  function resetNavigationState() {
    isNavigating.value = false
    historyStack.value = []
    currentIndex.value = -1
  }

  return {
    // State
    isNavigating: readonly(isNavigating),
    historyStack: readonly(historyStack),
    currentIndex: readonly(currentIndex),
    navigationTrackingInitialized: readonly(navigationTrackingInitialized),

    // Computed
    canGoBack,
    canGoForward,

    // Actions
    initializeRouterTracking,
    initializeNavigationTracking,
    getRouter,
    resetNavigationState,
  }
})
